import type { GoldHoldingsResponse, GoldHoldingRow, PriceData, EnrichedSynthNavData } from '../api/client'
import { resolveMarketReturn } from '../utils/marketReturn'
import { isInActualEtRth } from '../utils/etRth'

export interface GoldEnrichedDetail {
  prev_close: number | null
  imr_zscore: number | null
  imr_excluded_reason: string | null
  prev_return_pct?: number | null
  // NAV-share column arc (Task 2, 2026-09-05): per-holding GROSS GBP-
  // converted T/T-1 return published by Task 1 on synth_nav:gold:enriched's
  // holdings_detail. See client.ts's EnrichedHoldingPrice.return_pct_gbp
  // for the exact semantics (gross — not renormalised, not net of drag).
  return_pct_gbp?: number | null
  // RET% column parity fix (fix/holdings-panel-ret-pct-latest-close,
  // 2026-09-06): the truly-latest native-ccy close, threaded through from
  // EnrichedHoldingPrice.latest_close_native (see client.ts for the exact
  // semantics — ignores the T-1 lag convention prev_close honours for
  // non-NY holdings). Mirrors computeGoldTodaysMovePct's fallback (PR #388)
  // so the HoldingsPanel RET% column and the PnlPanel Today figure agree.
  latest_close_native?: number | null
  // JH-parity fix (2026-09-10): completed-session anchors for the
  // two-window mkt/ah decomposition. mkt = session_close/session_prev_close - 1
  // (T-2 -> T-1), ah = cur/session_close - 1 (T-1 -> now).
  session_close?: number | null
  session_prev_close?: number | null
}

export interface GoldEnrichedInput {
  staleDays: number
  detailByTicker: Record<string, GoldEnrichedDetail>
}

export interface GoldRow {
  ticker: string
  name: string
  weightPct: number | null
  bid: number | null
  ask: number | null
  spread_bps: number | null
  live: boolean
  source: 'pepperstone' | 'databento' | null
  venue: string | null
  currency: string | null
  isCash: boolean
  prev_close: number | null
  imr_zscore: number | null
  // JH-parity fix (2026-09-10): the compound of mkt_return and ah_return —
  // the completed-session move (session_close/session_prev_close - 1)
  // compounded with the live drift since that close (cur/session_close -
  // 1). Diverges from mkt_return whenever there is live drift, even when
  // the hybrid publisher covers the ticker — see mkt_return below.
  ret_pct: number | null
  // JH-parity fix (2026-09-10): the completed-session return alone
  // (session_close/session_prev_close - 1, i.e. T-2 -> T-1), computed via
  // resolveMarketReturn — the same helper BGA/JH/GGE use. ah_return carries
  // the separate live-quote drift beyond that close (cur/session_close -
  // 1); the two compound into ret_pct above. mkt_return no longer equals
  // ret_pct when the hybrid publisher covers the ticker — that whole-move
  // override (2026-09-05) was replaced by this two-window decomposition.
  mkt_return: number | null
  ah_return: number | null
  priced_from: 'quote' | 'close' | null
  // SRC column (HoldingsPanel unify, Task 2, 2026-09-04): mirrors BGA's
  // EnrichedHoldingPrice.source semantics so the shared SRC cell/legend can
  // render either fund's rows uniformly. Derived from the holdings-hybrid
  // per-ticker state when the 60s hybrid publisher covers this ticker
  // (state='live' -> 'live'; state='prxy' -> 'proxy', or 'after_hours' when
  // adjFrac is exactly 0 — gc_stale / anchor missing, see goldHybrid.ts);
  // otherwise falls back to the live-quote heuristic. state='delayed' ->
  // 'delayed' (2026-10-15: Toronto-only names on EODHD delayed quotes).
  // Gold never emits 'index_proxy' — the wider union just matches BGA's type.
  src: 'live' | 'delayed' | 'proxy' | 'after_hours' | 'index_proxy'
  // NAV-share column arc (Task 2, 2026-09-05): passed straight through from
  // the enriched-payload detail's return_pct_gbp (the daily telemetry
  // publisher, NOT the 60s hybrid payload — the hybrid payload never had
  // this field). Null when the enriched payload is unavailable, doesn't
  // cover this ticker, or the field itself is absent (old publisher).
  return_pct_gbp: number | null
}

// A quote is "live" when its timestamp is within this window; older quotes
// still render (BGA parity: last-known value, stale dot) but grey the dot.
// Entries with no timestamp at all (Pepperstone cache) keep their historical
// presence-equals-live semantics.
const FRESH_MS = 30 * 60_000

function isFresh(p: PriceData): boolean {
  const ts = p.tsUtc ?? p.timestamp
  if (!ts) return true
  const parsed = Date.parse(ts)
  if (Number.isNaN(parsed)) return true
  return Date.now() - parsed <= FRESH_MS
}

/** Pure mapping used by HoldingsPanel's GOLD mode row projection. */
export function mapGoldHoldings(
  resp: GoldHoldingsResponse | null,
  prices: Record<string, PriceData> | undefined,
  enriched?: GoldEnrichedInput | null,
  // Holdings-hybrid per-ticker rows (Task 2, 2026-09-04) — pass
  // goldHybrid.value.rowsByTicker from HoldingsPanel. Absent/empty when the
  // hybrid publisher is inactive or doesn't cover a ticker; the SRC mapping
  // then falls back to the live-quote heuristic below.
  hybridRowsByTicker?: Map<string, GoldHoldingRow> | null,
): GoldRow[] {
  if (!resp?.available || !resp.holdings) return []
  return resp.holdings
    .filter(h => h.ticker != null)
    .map(h => {
      const ticker = h.ticker as string
      const isCash = h.venue === 'NONE'
      const quote = !isCash ? (prices?.[ticker] ?? null) : null
      const bid = quote?.bid ?? null
      const ask = quote?.ask ?? null
      const mid = quote?.mid ?? null
      const spread_bps =
        bid != null && ask != null && mid != null && mid !== 0
          ? ((ask - bid) / mid) * 10_000
          : null

      const detail = enriched?.detailByTicker?.[ticker] ?? null
      const prev_close = detail?.prev_close ?? null
      const staleDays = enriched?.staleDays ?? 0

      // Weekend fallback (2026-08-16): with no quote at all (expired keys /
      // market closed) the row shows the last completed session close and
      // that session's return from the telemetry payload — BGA parity —
      // while the payload is fresh enough (staleDays <= 1).
      const useCloseFallback =
        quote == null && !isCash && prev_close != null && staleDays <= 1
      const imr_zscore =
        detail != null && detail.imr_excluded_reason == null && staleDays <= 1
          ? detail.imr_zscore
          : null
      // Spec "Failure modes": RET%/DIR keep working off the last known
      // prev_close only while stale_days <= 1; beyond that the payload is
      // too old to extrapolate against and both fall back to '--' (null).
      //
      // JH-parity fix (2026-09-10): mkt/ah now route through
      // resolveMarketReturn — the same helper BGA/JH/GGE use — with the
      // hybrid publisher's price threaded as the live `cur` input. mkt then
      // anchors on the completed session (session_close/session_prev_close
      // - 1) and ah measures the live drift beyond that close (cur /
      // session_close - 1) — the JH two-window semantics rather than the
      // whole-live-move override this block used to compute (which, under
      // the hybrid path, suppressed ah entirely — ba806107's regression).

      // SRC mapping / RET% live-price source (Task 2, 2026-09-04 spec /
      // live-RET% fix, 2026-09-05): the hybrid row, when present, takes
      // precedence over the live-quote heuristic — it reflects the
      // publisher's own live/proxy classification, which can disagree with
      // the client's quote-freshness check (e.g. a fresh quote the hybrid
      // publisher still treats as proxied pending its next tick).
      const hybridRow = hybridRowsByTicker?.get(ticker) ?? null
      const hybridPrice = hybridRow?.price ?? null
      const cur = hybridPrice ?? mid

      // rthOpen client-side: matches HoldingsPanel.vue's GGE fallback
      // convention (isInActualEtRth) — gold's enriched payload does not
      // carry us_rth_open per-holding.
      const rthOpen = isInActualEtRth()

      // Spec "Failure modes" gate: beyond stale_days > 1 the payload is too
      // old to extrapolate against — mkt/ah/ret_pct all fall back to null
      // (or the legacy/close-fallback values, gated the same way below).
      //
      // Stale session-anchor gate (2026-09-24, paolo): the two-window
      // decomposition needs stale_days == 0, NOT <= 1. A republished payload
      // (_republish_stale in gold_instrument_mr.py clones the previous
      // payload and only recomputes stale_days) still carries the
      // session_close/session_prev_close pair from the build it was cloned
      // from, so at stale_days >= 1 those anchors describe a session that is
      // no longer the last completed one. `mkt` then reports an old session
      // as if it were current and `ah`, measured as cur/session_close - 1,
      // swallows every session that has closed since. Observed 2026-09-24:
      // GFI rendered mkt +2.31% (the 09-22 session) and ah -5.07% (the whole
      // 09-23 session, which actually closed -4.98%), compounding to a RET%
      // of -2.88% that was really a 09-21 -> now two-session return. The
      // legacy/close fallbacks below stay at <= 1 — they are single-value
      // completed-session returns with no live `cur` compounded into them,
      // so a day-old one is stale but not structurally wrong.
      const resolved =
        staleDays === 0
          ? resolveMarketReturn({
              cur,
              t1Close: prev_close,
              prevClose: prev_close,
              sessionClose: detail?.session_close ?? null,
              sessionPrevClose: detail?.session_prev_close ?? null,
              rthOpen,
            })
          : { marketReturn: null, afterHoursReturn: null }

      // Legacy historical fallback (pre-JH-parity fixes, 2026-08-16 /
      // 2026-09-05 / 2026-09-06): resolveMarketReturn needs either
      // session_close/session_prev_close, or a live `cur`, to produce a
      // value. Old-publisher payloads (Task 1 — 2026-09-10 — now always
      // populates session_close/session_prev_close, but cached/older
      // payloads may still lack them) with no live quote at all and no
      // hybrid coverage have neither — fall back to the daily telemetry
      // payload's own last-session return (GBP-basis preferred, then
      // native, then the latest-close-native parity fix) so those payloads
      // keep rendering instead of going blank. resolveMarketReturn only
      // produces a value from a live `cur` when it has a usable
      // prev_close/t1Close to anchor against (e.g. test fixture: hybrid
      // covers the ticker but prev_close is null) — this fallback covers
      // that gap too, exactly as the pre-JH-parity code did (its usedHybrid
      // branch likewise required prev_close to be present and positive).
      const legacyMktFallback =
        staleDays <= 1
          ? detail?.latest_close_native != null &&
            prev_close != null &&
            prev_close > 0 &&
            detail.latest_close_native !== prev_close
            ? (detail.latest_close_native as number) / (prev_close as number) - 1
            : (detail?.return_pct_gbp ?? detail?.prev_return_pct ?? null)
          : null
      const mkt_return = resolved.marketReturn ?? legacyMktFallback
      const ah_return = resolved.afterHoursReturn
      const ret_pct =
        ah_return != null && mkt_return != null
          ? (1 + mkt_return) * (1 + ah_return) - 1
          : mkt_return != null && (resolved.marketReturn != null || useCloseFallback)
            ? mkt_return
            : null

      const live = quote != null && isFresh(quote)
      const src: GoldRow['src'] = hybridRow
        ? hybridRow.state === 'live'
          ? 'live'
          : hybridRow.state === 'delayed'
            ? 'delayed' // Toronto-only name on its EODHD delayed quote (2026-10-15)
            : hybridRow.adjFrac === 0
              ? 'after_hours' // gc_stale / anchor missing
              : 'proxy'
        : live
          ? 'live'
          : 'after_hours'

      return {
        ticker,
        name: h.name ?? '',
        weightPct: h.weightPct ?? null,
        bid: useCloseFallback ? prev_close : bid,
        ask: useCloseFallback ? prev_close : ask,
        spread_bps,
        live,
        source: (quote?.source as GoldRow['source']) ?? null,
        priced_from: quote != null ? ('quote' as const) : useCloseFallback ? ('close' as const) : null,
        venue: h.venue,
        currency: h.currency,
        isCash,
        prev_close,
        imr_zscore,
        ret_pct,
        mkt_return,
        ah_return,
        src,
        return_pct_gbp: detail?.return_pct_gbp ?? null,
      }
    })
    .sort((a, b) => (b.weightPct ?? 0) - (a.weightPct ?? 0))
}

// PnlPanel "Today" column / Synthetic-staleness fix (2026-09-06, paolo):
// gold's `overnight_return_gbp` on `synth_nav:gold:enriched` is T-1 aligned
// (gold_instrument_mr.py's `_prev_session_return` compares the last two
// COMPLETED sessions — see that file's docstring at lines 198-225) rather
// than a live intraday move, unlike BGA/JH/GGE whose shared synth_nav.py
// engine recomputes `overnight_return_gbp` on every live Pepperstone/
// databento equity tick (`set_live_price`). This derives gold's own live
// equivalent client-side from the 60s GoldHoldingsHybridService publisher
// (`synth_nav:gold:holdings_latest`, Arc B) exactly as the RET% live fix
// (2026-09-05, commit 1c8a2243 / mkt_return above) does per-holding, then
// aggregates it the same way the daily engine aggregates its own stale
// figure, so the two remain comparable:
//
//   move = (Σ weight_i × r_i) / covered_weight_frac − daily_drag_frac
//
// where r_i is the hybrid publisher's live-or-PRXY price vs prev_close for
// tickers it covers (native ccy — no FX conversion, matching the RET%
// column's own known simplification: gold's enriched payload carries no
// live gbpusd_rate/ref to convert against), falling back to the stale
// GBP-converted return_pct_gbp (or native-ccy prev_return_pct) for tickers
// the hybrid publisher doesn't cover. Returns null when the enriched
// payload is unavailable/too stale (stale_days > 1) or covered_weight_frac
// is missing/zero — same failure-mode gate goldHoldingsRows uses elsewhere.
export function computeGoldTodaysMovePct(
  en: Pick<EnrichedSynthNavData, 'stale_days' | 'covered_weight_frac' | 'daily_drag_frac' | 'holdings_detail' | 'sell_pending_at_strike'> | null | undefined,
  hybridRowsByTicker?: Map<string, GoldHoldingRow> | null,
  opts?: { sellPending?: boolean },
): number | null {
  if (!en || !en.holdings_detail || en.holdings_detail.length === 0) return null
  const staleDays = en.stale_days ?? 0
  if (staleDays > 1) return null
  const covered = en.covered_weight_frac ?? null
  if (covered == null || covered === 0) return null
  const drag = en.daily_drag_frac ?? 0

  // Strike-freeze gate (2026-09-21 paolo P&L defect fix). When a SELL has
  // been booked for today's strike, the position no longer has exposure to
  // post-noon moves — the Today figure must lock at the fund's valuation
  // point. Prefer the explicit opts.sellPending (PnlPanel passes it from
  // enrichedNav.sell_pending_at_strike) but fall through to the payload's
  // own flag so a direct caller (tests, useSignalPipeline) still sees the
  // freeze without threading the option through. When the flag is on AND
  // the hybrid row carries a strikePrice for the ticker, use that as the
  // numerator; otherwise the per-holding cascade falls through to today's
  // live behaviour (no regression pre-strike or when the freeze snapshot is
  // missing — e.g. Redis unwired, or the container came up after 12:00
  // without a prior capture).
  const sellPending = opts?.sellPending ?? en.sell_pending_at_strike ?? false

  let weightedSum = 0
  let any = false
  for (const h of en.holdings_detail) {
    if (h.ticker == null || h.weight == null) continue
    const prevClose = h.prev_close ?? null
    const hybridRow = hybridRowsByTicker?.get(h.ticker) ?? null
    const strikePrice = hybridRow?.strikePrice ?? null
    const hybridPrice = hybridRow?.price ?? null
    let r: number | null
    if (sellPending && strikePrice != null && prevClose != null && prevClose > 0) {
      // Strike-frozen: this is the ticker's price at the fund's 12:00 UK
      // valuation, captured by GoldHoldingsHybridService on the first
      // past-strike tick and pinned in Redis for the day.
      r = strikePrice / prevClose - 1
    } else if (hybridPrice != null && prevClose != null && prevClose > 0) {
      r = hybridPrice / prevClose - 1
    } else if (h.latest_close_native != null && prevClose != null && prevClose > 0 && h.latest_close_native !== prevClose) {
      // Prefer the actual latest close (may be Fri; will show Fri session move
      // if Fri data landed) over the T-1 aligned return_pct_gbp (Wed→Thu). When
      // latest === prev (no newer close available), no useful signal — fall
      // through to the historical return.
      r = (h.latest_close_native as number) / (prevClose as number) - 1
    } else {
      r = h.return_pct_gbp ?? h.prev_return_pct ?? null
    }
    if (r == null) continue
    weightedSum += (h.weight / 100) * r
    any = true
  }
  if (!any) return null
  return weightedSum / covered - drag
}
